import { useQuery } from '@tanstack/react-query';
import { resolvePeriod } from '@/features/reports/settings';
import { api } from './api';
import { useOrgId } from './org';
import type { VatReturnSummary } from './vat';

/** GET /orgs/:id/dashboard (document 06 D1). Amounts are decimal strings. */
export interface DashboardData {
  currency: string;
  today: string;
  period: { from: string; to: string };
  previous: { from: string; to: string };
  profitAndLoss: {
    income: string;
    expenses: string;
    netProfit: string;
    previous: { income: string; expenses: string; netProfit: string };
  };
  expenses: {
    total: string;
    top: Array<{ accountId: string; code: string; name: string; amount: string }>;
    other: string;
  };
  salesTrend: Array<{ month: string; from: string; to: string; income: string }>;
  tasks: DashboardTask[];
}

export interface DashboardTask {
  key:
    | 'sales_drafts'
    | 'purchase_drafts'
    | 'bank_review'
    | 'periods_to_lock'
    | 'einvoice_exceptions'
    | 'recurring_due';
  count: number;
  amount: string | null;
  through: string | null;
  staffOnly: boolean;
}

/** The dashboard's period selector (document 06 D1). */
export const DASHBOARD_PERIODS = [
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'this_quarter', label: 'This quarter' },
  { key: 'this_year', label: 'This financial year' },
] as const;
export type DashboardPeriod = (typeof DASHBOARD_PERIODS)[number]['key'];

export const isDashboardPeriod = (v: unknown): v is DashboardPeriod =>
  DASHBOARD_PERIODS.some((p) => p.key === v);

/** What the period is compared with, for the profit and loss card's legend. */
export const PREVIOUS_LABEL: Record<DashboardPeriod, string> = {
  this_month: 'Last month',
  last_month: 'Month before',
  this_quarter: 'Last quarter',
  this_year: 'Last financial year',
};

export function dashboardRange(
  period: DashboardPeriod,
  today: string,
  fyStartMonth: number,
): { from: string; to: string } {
  const r = resolvePeriod(period, today, fyStartMonth)!;
  return { from: r.from ?? today, to: r.to };
}

export function useDashboard(range: { from: string; to: string } | null) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['dashboard', orgId, range?.from, range?.to],
    queryFn: () =>
      api<DashboardData>(`/orgs/${orgId}/dashboard?from=${range!.from}&to=${range!.to}`),
    enabled: Boolean(orgId && range),
  });
}

// ---------- pure helpers (tested) ----------

/** Minor units of a decimal string ("-1234.5" → -123450n), for comparisons without floats. */
export function minorOf(decimal: string, exp = 2): bigint {
  const neg = decimal.startsWith('-');
  const [w = '0', f = ''] = (neg ? decimal.slice(1) : decimal).split('.');
  const v =
    BigInt(w || '0') * 10n ** BigInt(exp) + BigInt((f + '0'.repeat(exp)).slice(0, exp) || '0');
  return neg ? -v : v;
}

/**
 * Bar lengths as percentages of the largest value (negative values draw as zero). Display only:
 * the amounts shown next to the bars are the decimal strings themselves.
 */
export function barWidths(values: string[]): number[] {
  const m = values.map((v) => {
    const x = minorOf(v);
    return x > 0n ? x : 0n;
  });
  const max = m.reduce((a, b) => (b > a ? b : a), 0n);
  if (max === 0n) return m.map(() => 0);
  return m.map((x) => Number((x * 10000n) / max) / 100);
}

/** Percentage change from `before` to `now`, one decimal, or null when there was nothing before. */
export function percentChange(now: string, before: string): number | null {
  const a = minorOf(now);
  const b = minorOf(before);
  if (b === 0n) return null;
  const sign = b < 0n ? -1n : 1n;
  return Number(((a - b) * 1000n * sign) / b) / 10;
}

/** Points of a line chart over `width` × `height` with the baseline at zero. */
export function linePoints(
  values: string[],
  width: number,
  height: number,
): Array<{ x: number; y: number }> {
  const w = barWidths(values);
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  return w.map((pct, i) => ({
    x: Math.round(i * step * 10) / 10,
    y: Math.round((height - (pct / 100) * height) * 10) / 10,
  }));
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "2026-09" → "Sep" (and "Sep 2026" with the year). */
export const monthLabel = (ym: string, withYear = false): string =>
  `${MONTHS[Number(ym.slice(5, 7)) - 1] ?? ym}${withYear ? ` ${ym.slice(0, 4)}` : ''}`;

/** The VAT return the card shows: overdue first, then due, then the open period. */
export function currentVatReturn(returns: VatReturnSummary[]): VatReturnSummary | null {
  const rank: Record<string, number> = { overdue: 0, due: 1, open: 2, filed: 3 };
  return (
    [...returns]
      .filter((r) => r.status in rank)
      .sort(
        (a, b) => rank[a.status]! - rank[b.status]! || a.periodStart.localeCompare(b.periodStart),
      )[0] ?? null
  );
}

/** Task wording and where each task is done (Tasks card). */
export function taskView(
  t: DashboardTask,
): { label: string; to: string; search?: Record<string, string> } | null {
  const n = t.count;
  const s = n === 1 ? '' : 's';
  switch (t.key) {
    case 'sales_drafts':
      return {
        label: `${n} sales draft${s} to approve`,
        to: '/sales/invoices',
        search: { status: 'draft' },
      };
    case 'purchase_drafts':
      return { label: `${n} bill${s} and expense${s} to approve`, to: '/expenses' };
    case 'bank_review':
      return { label: `${n} bank transaction${s} to review`, to: '/banking' };
    case 'periods_to_lock':
      return {
        label: `${n} month${s} to lock`,
        to: '/accounting/periods',
      };
    case 'recurring_due':
      return {
        label: `${n} recurring transaction${s} need${n === 1 ? 's' : ''} attention`,
        to: '/accounting/recurring',
      };
    case 'einvoice_exceptions':
      return { label: `${n} e-invoice exception${s}`, to: '/taxes/e-invoicing' };
  }
}

/** Tasks to show: open ones only, and in the Business view only the client's own. */
export const visibleTasks = (tasks: DashboardTask[], isStaff: boolean): DashboardTask[] =>
  tasks.filter((t) => t.count > 0 && (isStaff || !t.staffOnly));

/** GET /practice/overview: compliance status per client (My practice › Clients). */
export interface PracticeOverviewRow {
  orgId: string;
  vat: {
    key: string;
    label: string;
    status: 'open' | 'due' | 'overdue' | 'filed' | 'paid';
    dueDate: string;
    netVat: string;
    overdueReturns: number;
  } | null;
  vatRegistered: boolean;
  bankItemsToReview: number;
  unreconciledAccounts: number;
  lastLockedPeriod: string | null;
  drafts: number;
  error?: string;
}

export function usePracticeOverview(enabled = true) {
  return useQuery({
    queryKey: ['practice', 'overview'],
    queryFn: () => api<PracticeOverviewRow[]>('/practice/overview'),
    enabled,
    staleTime: 60_000,
  });
}
